import {useState, useEffect, useRef, useMemo} from "react";

import { withTranslation } from "react-i18next";
import { Box, TextField } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Snackbar from "@plannel/components/Snackbar"
import { isEmpty } from "lodash";
import {AgGridReact} from "ag-grid-react";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import mpPsiSimulationService from "@plannel/services/mp/mp-psi-simulation-service"
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import {useDispatch} from "react-redux";
import {green} from "@mui/material/colors";

const MpProductionPlanDetailModal = (props) => {
  const { t, open, setOpen, title, nodeData, selectVersion } = props;
  const modalTitle = isEmpty(title) ? t("productionPlanDetail") : title;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [locName, setLocName] = useState("");
  const [workcenter, setWorkcenter] = useState("");
  const [resource, setResource] = useState("");
  const [itemCd, setItemCd] = useState("");
  const [itemName, setItemName] = useState("");
  const [productionPlanData, setProductionPlanData] = useState([]);

  const detailGridRef = useRef();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cellClicked = (filter) => {
    const reduxKey = "ORDER_TRACKING";
    const cellClickedReduxState = reduxUtil.createObjectState(reduxKey, { riskCd: null, itemSearch: null, currentPage: 1, pageSize: 100, ...filter });
    reduxDispatch(reduxUtil.updateViewState(cellClickedReduxState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const cellRenderer = (params) => {
    const { value } = params;
    const filter = { orderCd: [value], mpVersion: selectVersion };

    // 주문 추적이 권한 밖이면 링크가 아니라 평문으로 — 밑줄·포인터까지 함께 뺀다.
    if (!isPermitted("ORDER_TRACKING")) return <div>{value}</div>;

    return (
      <div style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={() => cellClicked(filter)} role="button" tabIndex={0}>{value}</div>
    )
  }

  const columnDefs = [
    { headerName: "planSeq", field: "planSeqNum", type: ["nonEditableColumn"], width: 80,},
    { headerName: "orderCd", field: "orderCd", type: ["nonEditableColumn"], minWidth: 150, flex: 1, cellRenderer: (params) => cellRenderer(params)},
    { headerName: "orderSeq", field: "orderSeqNum", type: ["nonEditableColumn"], width: 80},
    { headerName: "dueDate", field: "dueDate", type: ["nonEditableColumn"], width: 110 },
    { headerName: "orderQty", field: "orderQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 110 },
    { headerName: "reqQty", field: "requiredQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 110},
    { headerName: "prodDate", field: "productionDate", type: ["nonEditableColumn"], width: 140,  pinned: "right" },
    { headerName: "productionQty", field: "productionQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"],
      cellStyle: { backgroundColor: green[50] }, width: 130, pinned: "right" },
  ];

  const getRequestParams = () => {
      const params = {
        locCd: nodeData?.locCd,
        itemCd: nodeData?.itemCd,
        resCd: nodeData?.resCd,
        versionId: selectVersion?.id,
        dateList: nodeData?.selectedCellId
      }
      return params;
  }

  const retrieveProductionPlan = () => {
    detailGridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();
    mpPsiSimulationService.getProductionPlan(params)
      .then(response => {
        const { results } = response.data;
        setProductionPlanData(results)
      })
      .catch(e => {
        setProductionPlanData([]);
        debug(e);
      })
      .finally(() => {
        detailGridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = params => {
    retrieveProductionPlan();
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName:"PRODUCTION_PLAN_DETAIL", initState: true });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (nodeData && open) {
      setLocName(nodeData?.locNm);
      setItemCd(nodeData?.itemCd);
      setItemName(nodeData?.itemNm);
      setWorkcenter(nodeData?.workCenterCd);
      setResource(nodeData?.resCd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isEmpty(productionPlanData)) {
      GridUtils.setColumnDefs({ ...detailGridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionPlanData]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* 읽기 전용이라 onSave 를 주지 않는다 — 셸이 저장 버튼을 감추고 닫기 버튼만 남긴다 */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={modalTitle}
        cancelLabel={t("close")}
        width={1032}>
          <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                name="locName"
                label={t("location")}
                value={locName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="workcenter"
                label={t("workcenter")}
                value={workcenter || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="resource"
                label={t("resource")}
                value={resource || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                name="itemCd"
                label={t("itemCd")}
                value={itemCd || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
              <TextField
                size="small"
                name="itemName"
                label={t("itemName")}
                value={itemName || "-"}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1, backgroundColor: "#f5f5f5" }}
              />
            </Box>
          </Box>
          <Box className="ag-theme-balham grid-area" sx={{ mb: 1, height: "250px" }}>
            <AgGridReact
              ref={detailGridRef}
              rowData={productionPlanData}
              {...defaultGridMemo}
              statusBar={statusBar}
              onGridReady={onGridReady}
            />
          </Box>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
};

export default withTranslation()(MpProductionPlanDetailModal);